import React from "react";
import { createPortal } from "react-dom";

// Replaces AILoadingOverlay everywhere it was used (resume parsing on
// /profile/resume and inside ResumeUploadModal, job analysis on /analyze) —
// same full-screen portal pattern, now with a real stage checklist instead
// of a single spinner + message.
const VARIANTS = {
  resume: {
    title: "GuMi is building your intelligence layer...",
    // Deliberately no time estimate: real parses regularly run past the
    // 8-13s we used to promise (longer/more detailed resumes, model
    // latency), and a missed promise reads worse than no promise.
    note: "One-time setup. Every role you analyze next is scored against it.",
    steps: [
      { label: "Reading your resume", keys: ["reading"] },
      { label: "Extracting skills and seniority", keys: ["skills"] },
      { label: "Mapping your domain experience", keys: ["experience", "leadership"] },
      { label: "Preparing your intelligence layer", keys: ["scale", "complete"] },
    ],
  },
  analysis: {
    title: "Analyzing this role...",
    note: "Usually under 30 seconds.",
    steps: [
      { label: "Fetching the job posting", keys: ["fetching"] },
      { label: "Reading requirements", keys: ["parsing"] },
      { label: "Matching against your profile", keys: ["matching"] },
      { label: "Computing your signals", keys: ["scoring"] },
      { label: "Generating your briefing", keys: ["finishing", "complete"] },
    ],
  },
};

// `onCancel`, when given, shows a Cancel button so a stuck or unwanted run can be abandoned.
export default function StageLoader({ visible, variant = "resume", stage, percent, onCancel }) {
  if (!visible) return null;

  const config = VARIANTS[variant] || VARIANTS.resume;
  const activeIndex = Math.max(
    0,
    config.steps.findIndex((step) => step.keys.includes(stage))
  );
  const computedPercent = percent != null ? percent : Math.round(((activeIndex + 1) / config.steps.length) * 100);
  const clampedPercent = Math.max(0, Math.min(100, computedPercent));

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={config.title}
      data-testid="stage-loader"
      className="stage-loader-overlay"
    >
      <div className="stage-loader-card">
        <p className="stage-loader-wordmark">
          Gu<span className="stage-loader-wordmark-accent">Mi</span>
        </p>

        <h2 className="stage-loader-title" data-testid="stage-loader-title">
          {config.title}
        </h2>

        <ul className="stage-loader-list" data-testid="stage-loader-stages">
          {config.steps.map((step, index) => {
            const status = index < activeIndex ? "complete" : index === activeIndex ? "active" : "pending";
            return (
              <li key={step.label} className={`stage-loader-row stage-loader-row-${status}`} data-testid={`stage-loader-row-${index}`}>
                <span className={`stage-loader-dot stage-loader-dot-${status}`} aria-hidden="true">
                  {status === "complete" ? "✓" : ""}
                </span>
                <span className="stage-loader-label">{step.label}</span>
              </li>
            );
          })}
        </ul>

        <div className="stage-loader-track">
          <div className="stage-loader-fill" style={{ width: `${clampedPercent}%` }} data-testid="stage-loader-progress-bar" />
        </div>

        <p className="stage-loader-note">{config.note}</p>

        {onCancel && (
          <button type="button" onClick={onCancel} data-testid="stage-loader-cancel" className="stage-loader-cancel">
            Cancel
          </button>
        )}
      </div>

      <style>{`
        .stage-loader-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          padding: 24px;
        }
        .stage-loader-card {
          width: 100%;
          max-width: 420px;
          background: #ffffff;
          border-radius: 20px;
          padding: 36px;
          box-shadow: 0 32px 80px rgba(0, 0, 0, 0.25);
          font-family: 'Inter', sans-serif;
        }
        .stage-loader-wordmark {
          font-weight: 700;
          font-size: 16px;
          letter-spacing: -0.02em;
          color: #1a1a18;
          margin: 0 0 20px;
        }
        .stage-loader-wordmark-accent { color: #1D9E75; }
        .stage-loader-title {
          font-weight: 700;
          font-size: 20px;
          color: #1a1a18;
          margin: 0 0 20px;
          line-height: 1.3;
        }
        .stage-loader-list {
          list-style: none;
          margin: 0 0 20px;
          padding: 0;
        }
        .stage-loader-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 0;
          font-size: 14px;
        }
        .stage-loader-row-pending .stage-loader-label { color: #a3a19a; }
        .stage-loader-row-active .stage-loader-label { color: #1a1a18; font-weight: 500; }
        .stage-loader-row-complete .stage-loader-label { color: #8a8880; }
        .stage-loader-dot {
          width: 9px;
          height: 9px;
          min-width: 9px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .stage-loader-dot-pending { background: #d9d7d1; }
        .stage-loader-dot-active {
          background: #1D9E75;
          animation: stage-loader-pulse 1.4s ease-in-out infinite;
        }
        .stage-loader-dot-complete {
          background: #1D9E75;
          color: white;
          font-size: 7px;
          font-weight: 700;
          width: 14px;
          height: 14px;
          min-width: 14px;
          border-radius: 50%;
        }
        @keyframes stage-loader-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(29, 158, 117, 0.45); }
          50% { box-shadow: 0 0 0 5px rgba(29, 158, 117, 0); }
        }
        .stage-loader-track {
          height: 4px;
          border-radius: 2px;
          background: #e5e5e3;
          overflow: hidden;
        }
        .stage-loader-fill {
          height: 100%;
          border-radius: 2px;
          background: #1D9E75;
          transition: width 0.4s ease;
        }
        .stage-loader-note {
          margin: 14px 0 0;
          font-size: 12px;
          color: #8a8880;
        }
        .stage-loader-cancel {
          margin: 14px 0 0;
          padding: 0;
          background: none;
          border: none;
          font-size: 12.5px;
          color: #5f5e5a;
          text-decoration: underline;
          cursor: pointer;
        }
      `}</style>
    </div>,
    document.body
  );
}
